import {HttpResponse} from "@/types/http";
import {Session} from "@/types/core";
import {NTag, NTagLocation} from "@/types/ntag";
import axios, {AxiosRequestConfig, AxiosResponse} from "axios";
import {CoreClient, curCoreClient} from "@/services/CoreClient";

export class NtagClient {

  private ntagBaseUrl: string = process.env.VUE_APP_NTAG_API_BASE_URL!;

  private coreClient:CoreClient = curCoreClient;
  private currentSession:Session|null = null;

  private baseConfig: any = {
    timeout: 180000,
    validateStatus: () => true,
    headers: {}
  };

  public async getLocations(): Promise<GetLocationsReply & HttpResponse> {
    // we restore the currently stored session
    this.restoreSession();
    await this.coreClient.getInfo();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.ntagBaseUrl}/locations`,
      params: {
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);
    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, locations: resp.data as NTagLocation[]};
  }

  public async getLocation(locationId: string): Promise<GetLocationReply & HttpResponse> {
    // we restore the currently stored session
    this.restoreSession();
    await this.coreClient.getInfo();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.ntagBaseUrl}/locations/${locationId}`,
      params: {
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);
    if ( resp.data ) {
      return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, location: resp.data as NTagLocation};
    }

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async getTag(tagId: string): Promise<GetTagReply & HttpResponse> {
    // we restore the currently stored session
    this.restoreSession();
    await this.coreClient.getInfo();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.ntagBaseUrl}/tags/${tagId}`,
      params: {
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);
    if ( resp.data ) {
      return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, tag: resp.data as NTag};
    }
    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async getTags(): Promise<GetTagsReply & HttpResponse> {
    // we restore the currently stored session
    this.restoreSession();
    await this.coreClient.getInfo();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.ntagBaseUrl}/tags`,
      params: {
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);
    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, tags: resp.data as NTag[]};
  }

  public async getTagMovementsHistory(tagId: string):Promise<GetTagMovementsHistoryReply & HttpResponse> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.ntagBaseUrl}/movements/tag/${tagId}`,
      params: {
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);
    const movementHistory = resp.data as MoveEvent[];

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, movements: movementHistory};
  }

  public async updateLocationName(locationId: string, locationName: string): Promise<HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'put',
      url: `${this.ntagBaseUrl}/locations/${locationId}`,
      data: {
        name: locationName,
      },
    });

    const resp = await this.doRequest(cfg, 200);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async hideLocation(locationId: string): Promise<HttpResponse> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'put',
      url: `${this.ntagBaseUrl}/locations/${locationId}/hide`,
    });

    const resp = await this.doRequest(cfg, 200);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  private async doRequest(req: AxiosRequestConfig, expectedHttpCode?: number): Promise<InternalHttpReply> {
    let resp: AxiosResponse;

    try {
      resp = await axios.request(req);
    } catch (err) {
      return {errorMsg: `${err}`};
    }

    // we expect a code 200 by default
    expectedHttpCode = expectedHttpCode !== undefined ? expectedHttpCode : 200;

    if (resp.status !== expectedHttpCode) {
      if (resp.data && resp.data.message) {
        return {httpCode: resp.status, errorMsg: `${resp.data.message} (HTTP ${resp.status})`};
      } else {
        return {httpCode: resp.status, errorMsg: `unexpected HTTP error ${resp.status}`};
      }
    }
    return Promise.resolve({httpCode: resp.status, data: resp.data});
  }

  private restoreSession() {
    const ret = localStorage.getItem('v3-nnnco-session');

    if(!ret)
      return;

    let parsed: Session | undefined;
    try {
      parsed = JSON.parse(ret) as Session;
    } catch (err) {
      this.clearSession();
      return {};
    }

    // if we don't have a session or if it has expired let's clear the session
    if(!parsed.token || (parsed.expiresAt * 1000) < new Date().getTime())
      return this.clearSession();

    this.currentSession = parsed;

    // we restore the session
    this.baseConfig.headers = {authorization: `Bearer ${parsed.token}`};
  }

  private clearSession() {
    this.baseConfig.headers = {};
    this.currentSession = null;
    localStorage.removeItem('v3-nnnco-session');
  }
}

export interface GetTagsReply {
  tags: NTag[];
}

// get tag
export interface GetTagReply {
  tag?: NTag;
}

// get location
export interface GetLocationReply {
  location?: NTagLocation;
}

export interface GetLocationsReply {
  locations: NTagLocation[];
}

export interface GetTagMovementsHistoryReply {
  movements: MoveEvent[];
}

export interface MoveEvent {
  locationId: string;
  location: NTagLocation;
  tagId: string;
  tag?: NTag;
  type: 'in' | 'out';
  cause: 'missed_uplinks' | 'new_location' | 'unknown_base_station';
  ts: number;
  outEstimationTs?: number;
  lastOnlineInLocationTs?: number;
  compressionNumber?: number;
}

interface InternalHttpReply {
  httpCode?: number;
  errorMsg?: string;
  data?: object;
}

export const curNtagClient = new NtagClient();
